import { useState } from "react";
import { useAuth } from "@/shared/lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Logo from "@/shared/components/Logo";

/**
 * MIPL-branded sign-in screen.
 *
 * Layout: split-pane on desktop, stacked on mobile.
 *  - Left pane: brand panel — large logo + premium tagline copy.
 *  - Right pane: clean credential form with brand-orange focus + CTA.
 *
 * The hero copy is the official MIPL positioning statement, set in an
 * editorial serif (`.font-serif-display`) for a calm, premium tone.
 */
export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/");
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.15fr_1fr]">
      {/* ─── Left brand panel ─── */}
      <div className="hidden lg:flex relative bg-brand-surface px-12 py-10 flex-col justify-between overflow-hidden">
        {/* Soft warm decorative blobs that echo the logo backdrop */}
        <div className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-amber-200/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-orange-200/40 blur-3xl pointer-events-none" />

        {/* Top: lockup */}
        <div className="relative z-10">
          <Logo size={56} showWordmark={true} tone="dark" />
        </div>

        {/* Middle: tagline */}
        <div className="relative z-10 max-w-xl space-y-7" data-testid="login-tagline">
          <h1 className="font-serif-display text-[2.6rem] sm:text-[3.2rem] leading-[1.05] text-zinc-900 font-medium">
            Integrated brand implementation
            for spaces that&nbsp;<em className="text-orange-700 not-italic">communicate, guide, and inspire</em>.
          </h1>

          <p className="text-zinc-700 text-base leading-relaxed">
            Designing and delivering brand communication systems that elevate
            customer experience across every environment.
          </p>
          <p className="text-zinc-600 text-base leading-relaxed">
            From concept to execution, MIPL brings brands to life through
            <span className="text-zinc-900"> signage</span>,
            <span className="text-zinc-900"> wayfinding</span>,
            <span className="text-zinc-900"> environmental graphics</span>, and
            <span className="text-zinc-900"> spatial communication</span>.
          </p>
        </div>

        {/* Bottom: discreet meta */}
        <div className="relative z-10 flex items-center justify-between text-[11px] font-mono text-zinc-500 tracking-wider border-t border-orange-200/60 pt-5">
          <span>MIPL GLOBAL</span>
          <span>SIGNAGE · WAYFINDING · ENVIRONMENTAL GRAPHICS</span>
        </div>
      </div>

      {/* ─── Right credential form ─── */}
      <div className="flex items-center justify-center px-6 py-10 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-7" data-testid="login-form">
          {/* Mobile lockup */}
          <div className="lg:hidden flex flex-col items-center gap-5">
            <Logo size={48} showWordmark={true} />
            <p className="text-center text-sm text-zinc-600 leading-relaxed font-serif-display italic"
               data-testid="login-tagline-mobile">
              Integrated brand implementation for spaces that communicate, guide, and inspire.
            </p>
          </div>

          <div className="space-y-1">
            <div className="text-[11px] font-mono text-orange-600 uppercase tracking-[0.22em]">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-zinc-500">Use your assigned MIPL credentials.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                autoComplete="email"
                className="mt-1.5 w-full h-12 px-3 border border-zinc-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none font-mono text-sm transition-all rounded-md"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">Password</label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                required
                autoComplete="current-password"
                className="mt-1.5 w-full h-12 px-3 border border-zinc-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none font-mono text-sm transition-all rounded-md"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-rose-600 border border-rose-200 bg-rose-50 px-3 py-2 rounded-md" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full h-12 btn-primary font-semibold tracking-wide uppercase text-sm disabled:opacity-50 rounded-md inline-flex items-center justify-center gap-2 group"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : (
              <>Sign In <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
